import { Model } from "@/types/a2i-media.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { isEmpty } from "lodash";
import { useEffect, useMemo } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { useDynamicModelSchema } from "./useDynamicModelSchema";
import { useSessionStorage } from "./useSessionStorage";

/**
 * @property {string} formKey - Unique key to identify the form in session storage
 */
type UseA2iFormProps = {
  formKey: string; // Unique key to identify the form in session storage
  selectedModel: Model | null; // The currently selected model, it can be image generation, video generation, remix, etc.
  dynamicDefualtValues?: Record<string, any>; // Optional dynamic default values that are computed during runtime to override the model's default values
};

export const useA2iForm = ({
  selectedModel,
  formKey,
  dynamicDefualtValues,
}: UseA2iFormProps): UseFormReturn<any> => {
  const unqueFormKey = `${formKey}-${selectedModel?.id}`;
  const { setSessionItem, getSessionItem } = useSessionStorage();
  const localStoredValues = getSessionItem(unqueFormKey);

  const { schema, defaultValues } = useMemo(() => {
    if (selectedModel) {
      return useDynamicModelSchema(selectedModel, dynamicDefualtValues || {});
    }
    return {
      schema: z.object({}),
      defaultValues: {},
    };
  }, [selectedModel?.id, dynamicDefualtValues]);

  // Merge local values with schema defaults
  const mergedDefaultValues = useMemo(() => {
    return {
      ...defaultValues,
      ...(localStoredValues || {}),
      ...dynamicDefualtValues,
    };
  }, [defaultValues, localStoredValues]);

  const form = useForm<any>({
    resolver: zodResolver(schema as z.ZodTypeAny),
    defaultValues: mergedDefaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    // Preserve the previous prompt and negative_prompt values when switching models
    const previousPromptValue = form.getValues("prompt") || "";
    const previousNegativePromptValue = form.getValues("negative_prompt") || "";

    form.reset(
      {
        ...mergedDefaultValues,
        prompt: previousPromptValue,
        negative_prompt: previousNegativePromptValue,
      },
      {
        keepValues: false,
        keepDefaultValues: false,
      }
    );
  }, [selectedModel?.id]);

  // Watch form values and persist to sessionStorage
  useEffect(() => {
    const subscription = form.watch(() => {
      try {
        const values = form.getValues();
        if (!unqueFormKey || isEmpty(values)) return;
        setSessionItem(unqueFormKey, values);
      } catch {
        // ignore quota or serialization errors
      }
    });
    return () => subscription.unsubscribe();
  }, [unqueFormKey]);

  return form;
};
